import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface GiveAccessDialogProps {
  open: boolean;
  onClose: () => void;
  sectionName: string;
  members: Array<{ principalId: string; label: string }>;
  /** Principals that currently have access to this section (pre-checked). */
  currentPrincipalIds: string[];
  onSave: (principalIds: string[]) => void | Promise<void>;
}

/**
 * Owner/admin dialog to grant one or more workspace members access to a section.
 * A member with access may manage the tasks inside the section and all of its
 * subsections — but not rename/delete the section itself.
 */
export function GiveAccessDialog({
  open,
  onClose,
  sectionName,
  members,
  currentPrincipalIds,
  onSave,
}: GiveAccessDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(currentPrincipalIds));
  const [saving, setSaving] = useState(false);

  // Re-seed the selection from the current grants each time the dialog opens.
  useEffect(() => {
    if (open) setSelected(new Set(currentPrincipalIds));
  }, [open, currentPrincipalIds]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave([...selected]);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Give access to &ldquo;{sectionName}&rdquo;</DialogTitle>
          <DialogDescription>
            Selected members can add and edit tasks in this section and its subsections. They
            can&rsquo;t rename or delete the section.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-64 space-y-1 overflow-y-auto py-1">
          {members.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No workspace members to grant access to.
            </p>
          ) : (
            members.map((m) => {
              // The checkbox and its text label are SIBLINGS (associated via
              // id/htmlFor), never nested — nesting the Radix checkbox inside a
              // <label> double-fires the toggle when the box itself is clicked,
              // so only the text appeared to work.
              const checkboxId = `give-access-${m.principalId}`;
              return (
                <div
                  key={m.principalId}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent"
                >
                  <Checkbox
                    id={checkboxId}
                    checked={selected.has(m.principalId)}
                    onCheckedChange={() => toggle(m.principalId)}
                    aria-label={`Give ${m.label} access`}
                  />
                  <label htmlFor={checkboxId} className="flex-1 cursor-pointer text-sm">
                    {m.label}
                  </label>
                </div>
              );
            })
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
